import { ModalBody, ModalContent, ModalFooter } from "@chakra-ui/react";
import { type ChainFullId } from "@umami/chains";
import { useGetCurrentSaplingViewingKeyAddress } from "@umami/state";
import { FormProvider } from "react-hook-form";

import { AmountPreviewField } from "./form/AmountPreviewField";
import { FromAddressPreviewField } from "./form/FromAddressPreviewField";
import { MemoPreviewField } from "./form/MemoPreviewField";
import { ToAddressPreviewField } from "./form/ToAddressPreviewField";
import { type SendShieldedFormValues } from "./types";
import { usePreviewShieldedTransaction } from "./usePreviewShieldedTransaction";
import { SignPageHeader } from "../../SendFlow/SignPageHeader";

import { FormSubmitButton } from "@/components/SendFlow/utils";

type PreviewTransactionPageProps = {
  formValues: SendShieldedFormValues;
  chainId: ChainFullId;
  isSaplingTransaction: boolean;
  transaction: string | unknown;
};

export const PreviewTransactionPage = ({
  formValues,
  chainId,
  isSaplingTransaction,
  transaction,
}: PreviewTransactionPageProps) => {
  const currentAccountSaplingAddress = useGetCurrentSaplingViewingKeyAddress();
  const { form, handleSubmit, isLoading } = usePreviewShieldedTransaction({
    chainId,
    transaction,
  });

  if (!currentAccountSaplingAddress) {
    return null;
  }

  const transactionType = isSaplingTransaction ? "Shielded Transaction" : "Unshield Transaction";
  const submitButtonText = isSaplingTransaction
    ? "Submit Shielded Transaction"
    : "Submit Unshield Transaction";

  return (
    <FormProvider {...form}>
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <SignPageHeader title={`Confirm ${transactionType}`} />
          <ModalBody gap="24px">
            <AmountPreviewField amount={formValues.amount} chainId={chainId} />
            <FromAddressPreviewField address={currentAccountSaplingAddress} />
            <ToAddressPreviewField address={formValues.toAddress} />
            {isSaplingTransaction && formValues.memo && <MemoPreviewField memo={formValues.memo} />}
          </ModalBody>
          <ModalFooter>
            <FormSubmitButton isLoading={isLoading} title={submitButtonText} />
          </ModalFooter>
        </form>
      </ModalContent>
    </FormProvider>
  );
};
